import React from 'react';
import styled from 'styled-components';

import Button from 'design-system/src/components/Button/Button';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { Badge } from 'design-system/src/components/Badge/Badge';

import { formatStandardDate } from 'shared-utils/utils/formatDate';

import { CardSection } from './sharedStyles.tsx';
import { Prescriber } from '../../../api';

export const CardContainer = styled.div<{ $paddingBottom?: string; $hasError?: boolean }>`
  background-color: var(--white);
  border-radius: var(--border-radius-medium);
  border: ${({ $hasError }) => ($hasError ? '2px solid var(--red-200)' : '1px solid var(--grayscale-200)')};
  // -1px margin allows us to increase the border width without shifting the card
  ${({ $hasError }) => $hasError && 'margin: -1px;'}
  container-name: card-container;
  container-type: inline-size;
  ${({ $paddingBottom }) => ($paddingBottom ? `padding-bottom: ${$paddingBottom}` : '')};
`;

const HorizontalCardSection = styled(CardSection)`
  flex-direction: row;
  align-items: flex-start;
  padding: 16px 16px 16px 24px;
  margin: 0;

  // we're using the card width to determine the breakpoint because that will
  // take whether or not the sidebar is open into account
  @container card-container (width < 600px) {
    flex-direction: column;
  }
`;

const HorizontalCardLabel = styled.div`
  width: 168px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const HorizontalCardBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  max-width: 100%;
`;

export const CardLabelText = styled(Typography).attrs({
  styledAs: 'bodySmallDMSans',
  weight: 600,
})``;

const InfoCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CardAction = styled.div`
  margin-left: auto;
`;

const InlineDataGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  max-width: 100%;

  > * {
    max-width: 100%;
    overflow: hidden;
    // just in case the user is on mobile and the email is super long
    text-overflow: ellipsis;
  }
`;

interface DataStringProps {
  /**
   * The value to display
   */
  value?: string;
  /**
   * The name used in the fallback message when value is falsy. If not provided, renders nothing when value is falsy.
   */
  name?: string;
}

/**
 * A component that displays a value, a fallback message, or nothing based on props
 */
const DataString = ({ value, name }: DataStringProps) => {
  if (value) {
    return <Typography styledAs="bodySmallDMSans">{value}</Typography>;
  }

  if (name) {
    return (
      <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
        No {name} provided
      </Typography>
    );
  }

  return null;
};

interface InlineDataRowProps {
  /**
   * Array of data items to display inline with dot separators
   */
  items: Array<{
    value?: string;
    name?: string;
  }>;
}

/**
 * A component that displays multiple data items on the same line with dot separators
 */
const InlineDataRow = ({ items }: InlineDataRowProps) => {
  // Filter out items that have neither value nor name
  const validItems = items.filter((item) => item.value || item.name);

  if (validItems.length === 0) return null;

  return (
    <InlineDataGroup>
      {validItems.map((item, index) => (
        <React.Fragment key={index}>
          <DataString value={item.value} name={item.name} />
          {index < validItems.length - 1 && (
            <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
              ·
            </Typography>
          )}
        </React.Fragment>
      ))}
    </InlineDataGroup>
  );
};

interface InfoCardProps {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  hasError?: boolean;
  badge?: React.ReactNode;
}

const InfoCard = ({ title, children, action, hasError, badge }: InfoCardProps) => (
  <CardContainer $hasError={hasError}>
    <HorizontalCardSection>
      <HorizontalCardLabel>
        <CardLabelText renderedAs="h2">{title}</CardLabelText>
        {badge}
      </HorizontalCardLabel>
      <HorizontalCardBody>{children}</HorizontalCardBody>
      {action && <CardAction>{action}</CardAction>}
    </HorizontalCardSection>
  </CardContainer>
);

interface PatientPrescriberInfoCardsProps {
  prescriber?: Prescriber;
  providerNpi: string;
  providerPracticeName: string;
  providerAddress: string;
  patientName?: string;
  patientDob?: string;
  patientSex?: string;
  patientPhoneLine?: string;
  patientEmailLine?: string;
  patientAddressLine?: string;
  onEditPatient: () => void;
  isEditDisabled: boolean;
  hasMissingPatientInfo: boolean;
}

export const PatientPrescriberInfoCards = ({
  prescriber,
  providerNpi,
  providerPracticeName,
  providerAddress,
  patientName,
  patientDob,
  patientSex,
  patientPhoneLine,
  patientEmailLine,
  patientAddressLine,
  onEditPatient,
  isEditDisabled,
  hasMissingPatientInfo,
}: PatientPrescriberInfoCardsProps) => {
  const noPatientInfo =
    !patientName && !patientDob && !patientSex && !patientPhoneLine && !patientEmailLine && !patientAddressLine;

  return (
    <InfoCardList>
      <InfoCard title="Prescriber">
        {prescriber && <CardLabelText marginBottom="4px">{prescriber.name}</CardLabelText>}
        <Typography styledAs="bodySmallDMSans">NPI: {providerNpi}</Typography>
        <Typography styledAs="bodySmallDMSans">{providerPracticeName}</Typography>
        <Typography styledAs="bodySmallDMSans">{providerAddress}</Typography>
      </InfoCard>

      <InfoCard
        title="Patient"
        hasError={hasMissingPatientInfo}
        badge={hasMissingPatientInfo ? <Badge variant="error">Missing info</Badge> : undefined}
        action={
          <Button
            size="sm"
            variant={hasMissingPatientInfo ? 'primary' : 'secondary'}
            onClick={onEditPatient}
            disabled={isEditDisabled}
          >
            Edit
          </Button>
        }
      >
        {noPatientInfo && (
          <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
            No patient info provided
          </Typography>
        )}

        {patientName && <CardLabelText marginBottom="4px">{patientName}</CardLabelText>}
        <InlineDataRow
          items={[{ value: patientDob ? `DOB: ${formatStandardDate(patientDob)}` : undefined }, { value: patientSex }]}
        />
        {!noPatientInfo && (
          <InlineDataRow items={[{ value: patientPhoneLine }, { value: patientEmailLine, name: 'email' }]} />
        )}
        <DataString value={patientAddressLine} />
      </InfoCard>
    </InfoCardList>
  );
};

export default PatientPrescriberInfoCards;
